import { useEffect } from "react";
import { Link } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/hooks/use-analytics";
import { getSaunaService, saunaServices, type SaunaSlug } from "@/data/sauna-services";
import { SaunaGallery } from "@/components/sauna-gallery";
import { SaunaRange } from "@/components/sauna-range";
import { SaunaLocationLinks } from "@/components/sauna-location-links";
import { useSaunaSeo } from "@/hooks/use-sauna-seo";
import { findSaunaPage } from "@/data/sauna-seo";
import { SaunaServiceLower } from "@/components/sauna-service-lower";

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// Ember: a warm counterpoint to VertexAir engineering blue
const ember = "hsl(18 82% 52%)";

export default function SaunaServicePage({ slug }: { slug: SaunaSlug }) {
  const s = getSaunaService(slug);
  useSaunaSeo(findSaunaPage(s.href)!);
  const reduce = useReducedMotion();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [s]);

  const rise = (d = 0) =>
    reduce
      ? {}
      : { initial: { opacity: 0, y: 14 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.45, delay: d } };

  return (
    <div className="flex flex-col min-h-[100dvh] bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden text-white" style={{ background: "linear-gradient(160deg, hsl(222 47% 11%) 0%, hsl(218 52% 16%) 55%, hsl(14 45% 18%) 130%)" }}>
          {(slug === "advise-design" || slug === "maintenance-repair" || slug === "supply") && (
            <img
              src={`${import.meta.env.BASE_URL}images/${slug === "supply" ? "sauna-supply-delivery.webp" : slug === "maintenance-repair" ? "sauna-maintenance-cover.webp" : "sauna-engineer-cover.webp"}`}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
            />
          )}
          <div aria-hidden className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 64px)" }} />
          <div aria-hidden className="absolute -right-40 -bottom-56 w-[560px] h-[560px] rounded-full opacity-40 blur-3xl" style={{ background: `radial-gradient(circle, ${ember}, transparent 65%)` }} />
          <div className="container relative mx-auto px-4 max-w-7xl py-12 lg:py-16 grid lg:grid-cols-12 gap-8 items-end">
            <motion.div className="lg:col-span-8" {...rise()}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60 mb-6" data-testid="text-sauna-eyebrow">{s.eyebrow}</p>
              <h1 className="font-display font-bold text-4xl sm:text-5xl md:text-7xl leading-[1.02] mb-8" data-testid="text-sauna-title">
                {s.headline}
                <br />
                <span style={{ color: "hsl(22 90% 66%)" }}>{s.headlineAccent}</span>
              </h1>
              <p className="text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed mb-10">{s.intro}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link href={`/quote?category=sauna&service=${encodeURIComponent(`Sauna — ${s.navTitle}`)}`} data-testid="link-sauna-hero-quote"
                  className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 font-bold text-white transition-transform motion-safe:hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  style={{ background: ember }}>
                  {s.ctaLabel} <ArrowRight className="w-5 h-5" />
                </Link>
                <Link href="/contact" data-testid="link-sauna-hero-contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 font-semibold border border-white/25 hover:bg-white/10 transition-colors">
                  Talk to an engineer
                </Link>
              </div>
            </motion.div>
            <motion.nav aria-label="Sauna services" className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-white/15 pt-6 lg:pt-0 lg:pl-8" {...rise(0.1)}>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">Sauna services</p>
              <ol className="space-y-1">
                {saunaServices.map((o) => {
                  const active = o.slug === s.slug;
                  return (
                    <li key={o.slug}>
                      <Link href={o.href} aria-current={active ? "page" : undefined} data-testid={`link-sauna-side-${o.slug}`}
                        className={`flex items-baseline gap-4 py-2.5 transition-colors ${active ? "text-white" : "text-white/55 hover:text-white"}`}>
                        <span className="font-mono text-xs" style={active ? { color: "hsl(22 90% 66%)" } : undefined}>{o.index}</span>
                        <span className="font-display text-lg font-semibold">{o.navTitle}</span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </motion.nav>
          </div>
        </section>

        {/* Focus areas */}
        <section className="py-12 lg:py-16">
          <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="font-mono text-sm" style={{ color: ember }}>{s.index} / 04</span>
              <h2 className="text-3xl md:text-4xl font-bold font-display mt-3 mb-4">What this covers</h2>
              <p className="text-muted-foreground leading-relaxed">The same engineers who design airflow and heating for homes and commercial buildings, applied to one very hot room.</p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
              {s.focus.map((f, i) => (
                <motion.div key={f.title} {...rise(i * 0.05)} className="bg-card p-7 lg:p-8" data-testid={`card-sauna-focus-${i}`}>
                  <div className="w-8 h-1 rounded-full mb-5" style={{ background: i % 2 ? "hsl(var(--primary))" : ember }} />
                  <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <SaunaRange />
        {slug === "supply" && <SaunaGallery slug={slug} outdoorInfrared />}
        <SaunaGallery slug={slug} />

        <section className="py-12 lg:py-16 bg-secondary/60 border-y border-border">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-12 max-w-xl">How it works</h2>
            <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {s.steps.map((st, i) => (
                <motion.li key={st.title} {...rise(i * 0.06)} className="relative pt-8 border-t-2" style={{ borderColor: i === 0 ? ember : "hsl(var(--border))" }}>
                  <span className="absolute -top-3.5 left-0 bg-secondary pr-3 font-mono text-sm font-semibold text-foreground/70">Step {i + 1}</span>
                  <h3 className="font-display font-bold text-xl mb-2">{st.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{st.body}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <SaunaServiceLower s={s} />

        {/* CTA */}
        <section className="bg-foreground text-background">
          <div className="container mx-auto px-4 max-w-7xl py-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-2">Tell us about your sauna.</h2>
              <p className="text-background/70">We will come back to you with practical next steps.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href={`/quote?category=sauna&service=${encodeURIComponent(`Sauna — ${s.navTitle}`)}`} data-testid="link-sauna-cta-quote">
                <Button size="lg" className="rounded-full h-14 px-8 font-bold w-full">Request a quote</Button>
              </Link>
              <a href="tel:02080870906" data-testid="link-sauna-cta-phone" onClick={() => trackEvent("call_click", "Sauna CTA", s.href)}
                className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 font-semibold border border-background/25 hover:bg-background/10 transition-colors">
                <Phone className="w-4 h-4" /> 020 8087 0906
              </a>
            </div>
          </div>
        </section>
        <SaunaLocationLinks serviceSlug={slug} basePath={import.meta.env.BASE_URL} />
      </main>
      <Footer />
    </div>
  );
}
